import * as THREE from 'three';
import earcut from 'earcut';
import { latLonToVec3 } from '../geo/latlon';
import { GLOBE_RADIUS } from '../core/globe';
import { tweenNumber, type Tween } from '../core/tween';
import type { CountryPolygonGroup, PolygonWithHoles } from '../borders';

/**
 * Country-fill rendering — a single batched mesh covering every country, with
 * a per-vertex RGBA color attribute that drives both color (gameplay state)
 * and per-country alpha (highlight / spotlight effects). One BufferGeometry,
 * one material, one draw call per frame regardless of how many countries
 * have visible fills. Design spec: docs/map-engine-design.md § 7b.
 *
 * Why batched: an earlier per-country-mesh design hit a soft cap on mid-tier
 * mobile GPUs at ~239 draw calls + state switches. Batching collapses that
 * to one and keeps the same per-country state API by writing to slices of
 * the shared color attribute (a small typed-array region per state change,
 * uploaded to GPU as a partial buffer update via `addUpdateRange`).
 *
 * State changes still tween smoothly because each entry remembers its
 * "live" RGBA (`current`); each tween step lerps from the live value to
 * the target and writes the new RGBA across this entry's vertex range.
 */

/**
 * Explicit fill states a caller can set. `'hover'` and `'base'` are
 * managed internally — callers never set them. Pass `null` to
 * {@link PolygonFillManager.setFillState} to clear back to base / hover.
 *
 * Priority (descending) when explicit state and hover both apply:
 *   any explicit state > hover > base
 * Only one explicit state can be set per country at a time — callers
 * pass `null` to clear it back to hover/base. The explicit-state types
 * themselves are siblings (no internal priority among them).
 */
/**
 * Per-country explicit fill state. Higher-priority states overwrite
 * lower ones in the priority stack (revealed > correct > incorrect >
 * selected > inactive > hover > base).
 *
 * `inactive` is for countries that are part of the visible map but
 * deliberately NOT in the current mode's pool — region-filtered quizzes
 * grey out the rest of the world so the player knows what's in scope.
 * Mode components also typically gate clicks on inactive countries, so
 * the visual cue + behavior align.
 */
export type FillState =
  | 'selected'
  | 'correct'
  | 'incorrect'
  | 'revealed'
  | 'inactive';

/**
 * Color tokens for each fill state. Defaults align with the design tokens
 * in docs/map-engine-design.md § 11 — `good` for correct, `warn` for
 * incorrect, `accent` for revealed. `base` / `hover` / `selected` are fill-
 * specific and tunable via debug panel before getting locked into the
 * design system.
 */
export interface FillColorTokens {
  base: string;
  hover: string;
  selected: string;
  correct: string;
  incorrect: string;
  revealed: string;
  /** Out-of-pool countries in region-filtered modes. Sits visually between
   *  the dark base sphere (#1a1f28) and the active gray fill (#4a5568) so
   *  inactive countries read as "present, but not part of this game". */
  inactive: string;
}

const DEFAULT_TOKENS: FillColorTokens = {
  base: '#4a5568',
  hover: '#7589a8',
  selected: '#5fb6cd',
  correct: '#7fb069',
  incorrect: '#d97757',
  revealed: '#d4a24c',
  inactive: '#252a32',
};

/**
 * Sphere radius for fill geometry. Sits between the texture sphere (1.0)
 * and border lines (1.001). The polygonOffset on the material handles
 * z-fighting against the texture; this small radius lift keeps fills
 * comfortably below borders even when polygonOffset is disabled for debug.
 */
const FILL_RADIUS = GLOBE_RADIUS * 1.0005;

/**
 * Maximum edge length in degrees before densification kicks in. 2° ≈ 220 km
 * of arc, fine enough that flat triangle interiors never cut visibly under
 * the curved sphere surface even at the closest zoom.
 */
const MAX_EDGE_STEP_DEG = 2;

/** Default opacity — fills tinted enough to be visible but the texture still reads through. */
const DEFAULT_FILL_OPACITY = 0.55;

/** Per-country state-color tween duration. */
const STATE_TRANSITION_MS = 200;

/** Layer-wide opacity tween duration. */
const OPACITY_TRANSITION_MS = 400;

type RGBA = [number, number, number, number];

interface FillEntry {
  iso: string;
  /** First vertex (in the batched geometry) belonging to this country. */
  vertexStart: number;
  /** Number of vertices belonging to this country. */
  vertexCount: number;
  explicitState: FillState | null;
  hovered: boolean;
  /** Live RGBA — what's currently in the color buffer for this entry. Updated each tween frame. */
  current: RGBA;
  colorTween: Tween | null;
}

/**
 * Pre-baked fill geometry — the heavy output of triangulating every country
 * polygon. Pure typed arrays + plain JS, no Three.js / WebGL references, so
 * the same instance can be cached at module scope and handed to a fresh
 * `PolygonFillManager` on each new engine without re-triangulating.
 *
 * `ranges` mirrors the per-country vertex slice info that
 * {@link FillEntry} stores — used to rebuild the entries map on
 * `buildFromBaked` so per-country state writes still target the right
 * vertices.
 */
export interface BakedFillData {
  positions: Float32Array;
  indices: Uint32Array;
  ranges: Array<{ iso: string; vertexStart: number; vertexCount: number }>;
}

/**
 * Splits a ring into segments no longer than `maxStepDeg` per axis. Without
 * this, a triangle whose vertices span (say) 30° latitude renders as a flat
 * chord under the sphere — visibly floating off the surface near its center.
 *
 * Edges crossing the antimeridian (|Δlon| > 90°) are NOT densified —
 * linear interpolation between (179, ...) and (-179, ...) would cut
 * across the globe. The antimeridian-triangle filter drops those triangles
 * after earcut runs, so leaving the original edge intact is fine.
 */
function densifyRing(ring: [number, number][], maxStepDeg: number): [number, number][] {
  if (ring.length < 2) return ring;
  const out: [number, number][] = [ring[0]];
  for (let i = 1; i < ring.length; i++) {
    const [lon0, lat0] = ring[i - 1];
    const [lon1, lat1] = ring[i];
    const dLon = lon1 - lon0;
    const dLat = lat1 - lat0;
    const span = Math.max(Math.abs(dLon), Math.abs(dLat));
    if (Math.abs(dLon) > 90 || span <= maxStepDeg) {
      out.push([lon1, lat1]);
      continue;
    }
    const steps = Math.ceil(span / maxStepDeg);
    for (let k = 1; k < steps; k++) {
      const t = k / steps;
      out.push([lon0 + dLon * t, lat0 + dLat * t]);
    }
    out.push([lon1, lat1]);
  }
  return out;
}

/**
 * Triangulate one polygon (outer + holes) in (lon, lat) space using earcut,
 * then recursively subdivide any triangle whose longest edge in lon/lat
 * space exceeds `maxStepDeg`. Subdivision is required because earcut
 * happily emits triangles spanning a country interior — projecting those
 * vertex-by-vertex onto the sphere leaves the triangle's flat interior
 * sagging well below the surface (and behind the base sphere at large
 * spans), so the country's middle would render empty.
 *
 * Returns the (possibly augmented) vertex list and the final triangle
 * indices, with antimeridian-spanning triangles already dropped. Caller
 * projects vertices to the sphere.
 */
function triangulatePolygon(
  p: PolygonWithHoles,
  maxStepDeg: number,
): { vertices: [number, number][]; triangles: number[] } {
  const outer = densifyRing(p.outer, maxStepDeg);
  const holes = p.holes.map((h) => densifyRing(h, maxStepDeg));

  const flat: number[] = [];
  const vertices: [number, number][] = [];
  const holeIndices: number[] = [];

  for (const v of outer) {
    flat.push(v[0], v[1]);
    vertices.push(v);
  }
  for (const hole of holes) {
    holeIndices.push(vertices.length);
    for (const v of hole) {
      flat.push(v[0], v[1]);
      vertices.push(v);
    }
  }

  const raw = earcut(flat, holeIndices.length > 0 ? holeIndices : undefined, 2);

  // Drop any triangle that genuinely spans the antimeridian. Lon span > 180°
  // is just the cheap pre-filter — by itself it would also reject Antarctica's
  // pole-region triangles, whose vertices wrap longitudinally but collapse to
  // nearly the same point on the sphere (since lat → ±90° projects to a single
  // point regardless of lon). When the lon-span trigger fires, fall back to a
  // 3D chord-length test: garbage antimeridian-crossers have a chord
  // approaching 2 (antipodal), pole-region triangles stay small. Filtering
  // BEFORE subdivision so we never compute midpoints on edges that
  // linear-interpolate across the wraparound.
  const filtered: number[] = [];
  for (let i = 0; i < raw.length; i += 3) {
    const a = vertices[raw[i]];
    const b = vertices[raw[i + 1]];
    const c = vertices[raw[i + 2]];
    const minLon = Math.min(a[0], b[0], c[0]);
    const maxLon = Math.max(a[0], b[0], c[0]);
    if (maxLon - minLon > 180 && maxChordSqOnSphere(a, b, c) > ANTIMERIDIAN_CHORD_SQ) {
      continue;
    }
    filtered.push(raw[i], raw[i + 1], raw[i + 2]);
  }

  const triangles = subdivideLargeTriangles(vertices, filtered, maxStepDeg);
  return { vertices, triangles };
}

/**
 * Squared chord-length threshold (on the unit sphere) above which a triangle
 * is treated as a real antimeridian crosser — i.e. its vertices are far
 * enough apart in 3D that no legitimate polygon edge could connect them.
 * 1.5² corresponds to ~97° of arc, comfortably above any pole-region
 * triangle's chord (which approaches 0 as vertices approach the pole).
 */
const ANTIMERIDIAN_CHORD_SQ = 1.5 * 1.5;

/** Longest squared 3D chord among the three edges of a triangle on the unit sphere. */
function maxChordSqOnSphere(
  a: [number, number],
  b: [number, number],
  c: [number, number],
): number {
  const va = latLonToVec3(a[1], a[0]);
  const vb = latLonToVec3(b[1], b[0]);
  const vc = latLonToVec3(c[1], c[0]);
  return Math.max(chordSq(va, vb), chordSq(vb, vc), chordSq(vc, va));
}

function chordSq(p: { x: number; y: number; z: number }, q: { x: number; y: number; z: number }): number {
  const dx = p.x - q.x;
  const dy = p.y - q.y;
  const dz = p.z - q.z;
  return dx * dx + dy * dy + dz * dz;
}

/**
 * Recursively split any triangle whose longest edge (in lon/lat space)
 * exceeds `maxEdgeDeg` into 4 sub-triangles via edge midpoints. Mutates
 * `vertices` to append midpoints; returns the new index list.
 *
 * Shared edges between adjacent triangles produce a single midpoint via
 * the cache, so neighboring triangles stay watertight (no T-junctions).
 *
 * The 1-to-4 split (Loop-style) keeps the angular character of each
 * resulting triangle the same as the parent, so we don't end up with
 * skinny slivers that earcut would have avoided.
 */
function subdivideLargeTriangles(
  vertices: [number, number][],
  indices: number[],
  maxEdgeDeg: number,
): number[] {
  const limitSq = maxEdgeDeg * maxEdgeDeg;
  const midpointCache = new Map<string, number>();

  function midpoint(a: number, b: number): number {
    const key = a < b ? `${a},${b}` : `${b},${a}`;
    const cached = midpointCache.get(key);
    if (cached !== undefined) return cached;
    const va = vertices[a];
    const vb = vertices[b];
    const idx = vertices.length;
    vertices.push([(va[0] + vb[0]) / 2, (va[1] + vb[1]) / 2]);
    midpointCache.set(key, idx);
    return idx;
  }

  function maxEdgeSq(a: number, b: number, c: number): number {
    const va = vertices[a];
    const vb = vertices[b];
    const vc = vertices[c];
    const e1 = Math.max((va[0] - vb[0]) ** 2, (va[1] - vb[1]) ** 2);
    const e2 = Math.max((vb[0] - vc[0]) ** 2, (vb[1] - vc[1]) ** 2);
    const e3 = Math.max((vc[0] - va[0]) ** 2, (vc[1] - va[1]) ** 2);
    return Math.max(e1, e2, e3);
  }

  // Explicit stack instead of recursion — country interiors can produce
  // deep splits and explicit control sidesteps any engine recursion limits.
  const stack: number[] = [...indices];
  const out: number[] = [];

  while (stack.length > 0) {
    const c = stack.pop() as number;
    const b = stack.pop() as number;
    const a = stack.pop() as number;
    if (maxEdgeSq(a, b, c) <= limitSq) {
      out.push(a, b, c);
      continue;
    }
    const ab = midpoint(a, b);
    const bc = midpoint(b, c);
    const ca = midpoint(c, a);
    stack.push(a, ab, ca);
    stack.push(ab, b, bc);
    stack.push(ca, bc, c);
    stack.push(ab, bc, ca);
  }
  return out;
}

/**
 * Triangulate every country in `groups` to produce the heavy intermediate
 * data needed for the batched fill mesh. Pure: no Three.js, no GPU work,
 * just typed arrays and plain JS — so the result is safe to cache at
 * module scope and reuse across MapEngine instances.
 *
 * Heavy: tens to hundreds of ms depending on device. The point of caching
 * is to skip this entire pass on cross-mode navigation.
 *
 * Each country always gets a `ranges` entry, even if it triangulated to
 * zero vertices, so per-country state-change calls remain a no-op rather
 * than an error for ISOs with no geometry.
 */
export function triangulateGroups(groups: CountryPolygonGroup[]): BakedFillData {
  const positions: number[] = [];
  const indices: number[] = [];
  const ranges: Array<{ iso: string; vertexStart: number; vertexCount: number }> = [];

  for (const group of groups) {
    const vertexStart = positions.length / 3;

    for (const poly of group.polygons) {
      const { vertices, triangles } = triangulatePolygon(poly, MAX_EDGE_STEP_DEG);
      if (triangles.length === 0) continue;
      const baseVtxIdx = positions.length / 3;
      for (const [lon, lat] of vertices) {
        const v = latLonToVec3(lat, lon, FILL_RADIUS);
        positions.push(v.x, v.y, v.z);
      }
      for (const idx of triangles) indices.push(baseVtxIdx + idx);
    }

    const vertexCount = positions.length / 3 - vertexStart;
    ranges.push({ iso: group.iso, vertexStart, vertexCount });
  }

  return {
    positions: new Float32Array(positions),
    indices: new Uint32Array(indices),
    ranges,
  };
}

/**
 * Single-mesh batched fill renderer for every country. Per-country state
 * changes are slice writes into a shared RGBA color attribute.
 */
export class PolygonFillManager {
  private readonly root: THREE.Group;
  private mesh: THREE.Mesh | null = null;
  private material: THREE.MeshBasicMaterial | null = null;
  private colorAttribute: THREE.BufferAttribute | null = null;
  private readonly entries = new Map<string, FillEntry>();
  private tokens: FillColorTokens;
  private currentOpacity = DEFAULT_FILL_OPACITY;
  private opacityTween: Tween | null = null;

  constructor(parent: THREE.Group, tokens?: Partial<FillColorTokens>) {
    this.root = new THREE.Group();
    this.root.name = 'fills.countries';
    // NOTE: deliberately do NOT set renderOrder on this group. Three.js uses
    // a group's renderOrder as `groupOrder` in its sort, which takes
    // precedence over each child mesh's own renderOrder. Setting it to 1
    // here would push the batched fills mesh into a higher groupOrder than
    // the borders mesh (which lives directly in globeGroup at groupOrder 0),
    // and borders would end up rendered BEFORE fills regardless of their
    // mesh-level renderOrder = 2. The per-mesh renderOrder values
    // (texture 0, fills 1, borders 2) handle layering correctly within
    // groupOrder 0.
    parent.add(this.root);
    this.tokens = { ...DEFAULT_TOKENS, ...tokens };
  }

  /**
   * Build (or rebuild) the batched mesh from every country in `groups`.
   * Triangulates every polygon synchronously, then delegates the GPU
   * upload to {@link buildFromBaked}. Idempotent — disposes the previous
   * mesh first. Heavy: tens to hundreds of ms.
   *
   * Cross-engine cache fast path: callers that already have
   * {@link BakedFillData} (e.g. cached from a previous engine) should
   * call {@link buildFromBaked} directly to skip the triangulation.
   */
  build(groups: CountryPolygonGroup[]): void {
    this.buildFromBaked(triangulateGroups(groups));
  }

  /**
   * Build the batched mesh from pre-triangulated geometry. The fast path
   * for cross-mode navigation: borders/cache.ts hands the same
   * {@link BakedFillData} to every new engine, so we skip the slow
   * triangulation and only pay for the GPU upload + per-vertex color
   * buffer allocation.
   *
   * Idempotent — disposes the previous mesh first. Resets the entries
   * map fresh from `baked.ranges` so per-country state writes target the
   * right vertex slices.
   */
  buildFromBaked(baked: BakedFillData): void {
    this.disposeMesh();

    const baseColor = new THREE.Color(this.tokens.base);
    const baseR = baseColor.r;
    const baseG = baseColor.g;
    const baseB = baseColor.b;

    const vertexCount = baked.positions.length / 3;
    // Per-vertex RGBA, all initialized to base color + full alpha. Alpha
    // stays at 1.0 so the material's `opacity` controls the layer-wide
    // fade; per-country alpha overrides flow through the same slice
    // writes via entry.current[3].
    const colors = new Float32Array(vertexCount * 4);
    for (let i = 0; i < vertexCount; i++) {
      const o = i * 4;
      colors[o] = baseR;
      colors[o + 1] = baseG;
      colors[o + 2] = baseB;
      colors[o + 3] = 1.0;
    }

    // Rebuild the entries map from the baked ranges. Every country in
    // `ranges` gets an entry even if vertexCount is 0 — keeps state
    // changes a no-op for ISOs that triangulated to nothing.
    this.entries.clear();
    for (const range of baked.ranges) {
      this.entries.set(range.iso, {
        iso: range.iso,
        vertexStart: range.vertexStart,
        vertexCount: range.vertexCount,
        explicitState: null,
        hovered: false,
        current: [baseR, baseG, baseB, 1.0],
        colorTween: null,
      });
    }

    if (baked.positions.length === 0) return;

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(baked.positions, 3));
    const colorAttr = new THREE.Float32BufferAttribute(colors, 4);
    // Hint the renderer this attribute updates often — gives the driver a
    // chance to allocate it in fast-update GPU memory.
    colorAttr.setUsage(THREE.DynamicDrawUsage);
    geometry.setAttribute('color', colorAttr);
    // Uint32 indices because total vertex count comfortably exceeds 65535
    // once subdivision densifies large countries.
    geometry.setIndex(new THREE.Uint32BufferAttribute(baked.indices, 1));

    // vertexColors:true tells MeshBasicMaterial to multiply the per-vertex
    // RGBA into the diffuse output. Material opacity multiplies on top of
    // vertex alpha — both layers compose naturally.
    // polygonOffset pushes fill toward the camera in depth so it never
    // z-fights with the texture sphere. depthWrite:false so borders drawn
    // on top (renderOrder 2) aren't hidden by an opaque fill pixel.
    // FrontSide (the default): the fills sit on a sphere — only the
    // camera-facing half is ever visible. Halves the fragment work vs
    // DoubleSide. Earlier we used DoubleSide as defensive insurance against
    // winding inversions; the antimeridian-triangle filter has been stable
    // in production so the back-side draw is wasted work.
    const material = new THREE.MeshBasicMaterial({
      vertexColors: true,
      transparent: true,
      opacity: this.currentOpacity,
      depthWrite: false,
      polygonOffset: true,
      polygonOffsetFactor: -1,
      polygonOffsetUnits: -1,
    });

    const mesh = new THREE.Mesh(geometry, material);
    mesh.name = 'fills.countries.batched';
    mesh.renderOrder = 1;
    this.root.add(mesh);

    this.mesh = mesh;
    this.material = material;
    this.colorAttribute = colorAttr;
  }

  /** Show or hide every fill at once. No animation — pair with setOpacity for fade-in/out. */
  setVisible(visible: boolean): void {
    this.root.visible = visible;
  }

  /** Whether the fills group is currently shown. */
  isVisible(): boolean {
    return this.root.visible;
  }

  /** Whether the batched mesh has been built. */
  isReady(): boolean {
    return this.mesh !== null;
  }

  /**
   * Set the layer-wide opacity. Tweens by default; pass `animate: false`
   * for an instant snap (useful when reapplying state on init).
   */
  setOpacity(target: number, animate = true): void {
    const clamped = Math.max(0, Math.min(1, target));
    this.opacityTween?.cancel();
    if (!this.material || !animate) {
      this.currentOpacity = clamped;
      if (this.material) this.material.opacity = clamped;
      return;
    }
    const start = this.currentOpacity;
    const mat = this.material;
    this.opacityTween = tweenNumber({
      from: start,
      to: clamped,
      ms: OPACITY_TRANSITION_MS,
      onUpdate: (v) => {
        this.currentOpacity = v;
        mat.opacity = v;
      },
    });
  }

  /** Current effective opacity (mid-tween reflects the live value, not the target). */
  getOpacity(): number {
    return this.currentOpacity;
  }

  /**
   * Set or clear the explicit state for one country. `null` reverts to
   * hover (if hovered) or base. Color crossfades over 200ms.
   */
  setFillState(iso: string, state: FillState | null): void {
    const entry = this.entries.get(iso);
    if (!entry) return;
    if (entry.explicitState === state) return;
    entry.explicitState = state;
    this.applyColor(entry);
  }

  /** Reset every country's explicit state. Hover state is preserved. */
  clearFillStates(): void {
    for (const entry of this.entries.values()) {
      if (entry.explicitState !== null) {
        entry.explicitState = null;
        this.applyColor(entry);
      }
    }
  }

  /**
   * Internal hover routing — called by the interaction dispatcher when the
   * pointer enters/leaves a country. Hover color only takes effect when no
   * explicit state is set (selected/correct/etc. always win).
   */
  setHoveredIso(iso: string | null): void {
    for (const entry of this.entries.values()) {
      const shouldBeHovered = iso !== null && entry.iso === iso;
      if (shouldBeHovered === entry.hovered) continue;
      entry.hovered = shouldBeHovered;
      if (entry.explicitState === null) this.applyColor(entry);
    }
  }

  /**
   * Override one or more color tokens. Existing fill states immediately
   * tween to the new colors. Used by the debug panel for live tuning and
   * by game configs for colorblind palettes.
   */
  setColorTokens(partial: Partial<FillColorTokens>): void {
    this.tokens = { ...this.tokens, ...partial };
    for (const entry of this.entries.values()) this.applyColor(entry);
  }

  /** Current color tokens (read-only snapshot). */
  getColorTokens(): FillColorTokens {
    return { ...this.tokens };
  }

  /** Free GPU resources. Engine calls this on dispose. */
  dispose(): void {
    this.opacityTween?.cancel();
    this.opacityTween = null;
    for (const entry of this.entries.values()) entry.colorTween?.cancel();
    this.entries.clear();
    this.disposeMesh();
    this.root.removeFromParent();
  }

  // -- internals --

  private disposeMesh(): void {
    if (!this.mesh) return;
    this.mesh.removeFromParent();
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
    this.mesh = null;
    this.material = null;
    this.colorAttribute = null;
  }

  private applyColor(entry: FillEntry): void {
    const colorAttr = this.colorAttribute;
    if (!colorAttr) return;

    const targetCss = this.targetColorFor(entry);
    const targetColor = new THREE.Color(targetCss);
    // Alpha stays at 1.0 — per-country opacity reuses the same RGBA slot
    // but is set independently (future API).
    const targetRGBA: RGBA = [targetColor.r, targetColor.g, targetColor.b, entry.current[3]];

    entry.colorTween?.cancel();

    const fromRGBA: RGBA = [
      entry.current[0],
      entry.current[1],
      entry.current[2],
      entry.current[3],
    ];
    const { vertexStart, vertexCount } = entry;
    const arr = colorAttr.array as Float32Array;

    entry.colorTween = tweenNumber({
      from: 0,
      to: 1,
      ms: STATE_TRANSITION_MS,
      onUpdate: (t) => {
        const r = fromRGBA[0] + (targetRGBA[0] - fromRGBA[0]) * t;
        const g = fromRGBA[1] + (targetRGBA[1] - fromRGBA[1]) * t;
        const b = fromRGBA[2] + (targetRGBA[2] - fromRGBA[2]) * t;
        const a = fromRGBA[3] + (targetRGBA[3] - fromRGBA[3]) * t;
        entry.current[0] = r;
        entry.current[1] = g;
        entry.current[2] = b;
        entry.current[3] = a;
        for (let i = 0; i < vertexCount; i++) {
          const offset = (vertexStart + i) * 4;
          arr[offset] = r;
          arr[offset + 1] = g;
          arr[offset + 2] = b;
          arr[offset + 3] = a;
        }
        // Mark just this entry's range dirty so the GPU upload only ships
        // the affected bytes rather than the whole color buffer.
        colorAttr.addUpdateRange(vertexStart * 4, vertexCount * 4);
        colorAttr.needsUpdate = true;
      },
    });
  }

  private targetColorFor(entry: FillEntry): string {
    if (entry.explicitState !== null) return this.tokens[entry.explicitState];
    if (entry.hovered) return this.tokens.hover;
    return this.tokens.base;
  }
}
